import React from 'react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { Widget } from '../Widget'
import { useChartPalette, ChartTooltipContent, formatShortDate, formatLongDate, ChartEmptyState } from './chartUtils'

export interface AreaChartSeries {
  key: string
  name: string
  color: string
  /** Area with a soft gradient fill (the chart's primary series) vs a bare line (secondary series, avoids stacking fills on different-scale values). */
  filled?: boolean
  /** Formats this series' tooltip value, e.g. formatCurrency for a dollar series. Defaults to toLocaleString(). */
  valueFormatter?: (value: number) => string
}

export interface AreaChartWidgetProps {
  title: string
  description?: string
  /** Right side of the header -- a period Select, a trend badge, whatever the caller needs. */
  headerRight?: React.ReactNode
  data: Record<string, any>[]
  xKey: string
  series: AreaChartSeries[]
  height?: number
  /** Dates ("YYYY-MM-DD") get human-readable formatting by default; set false for already-formatted/non-date x values. */
  dateAxis?: boolean
}

// Minimal styling throughout: grid is horizontal-only and barely visible,
// axes have no line/ticks (just muted labels), legend is a small dot+label
// row inside the chart's own surface (below the plot, not on the lighter
// shell outside it) instead of recharts' own boxier default. Mirrors
// admin-dashboard's AreaChartWidget so charts look identical across apps.
export default function AreaChartWidget({
  title,
  description,
  headerRight,
  data,
  xKey,
  series,
  height = 260,
  dateAxis = true,
}: AreaChartWidgetProps) {
  const palette = useChartPalette()
  const gradientIdBase = title.replace(/[^a-zA-Z0-9]/g, '')
  const valueFormatters = Object.fromEntries(
    series.filter((s) => s.valueFormatter).map((s) => [s.key, s.valueFormatter!])
  )

  return (
    <Widget>
      <Widget.Header>
        <Widget.Title>{title}</Widget.Title>
        {description && <Widget.Description>{description}</Widget.Description>}
        {headerRight}
      </Widget.Header>
      <Widget.Content>
        {data.length === 0 ? (
          <ChartEmptyState height={height} />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={height}>
              <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  {series.filter((s) => s.filled !== false).map((s) => (
                    <linearGradient key={s.key} id={`${gradientIdBase}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={palette.border} opacity={0.5} />
                <XAxis
                  dataKey={xKey}
                  stroke={palette.muted}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={30}
                  tickFormatter={dateAxis ? formatShortDate : undefined}
                />
                <YAxis stroke={palette.muted} fontSize={11} fontFamily="var(--font-mono)" tickLine={false} axisLine={false} width={36} />
                <Tooltip
                  content={<ChartTooltipContent labelFormatter={dateAxis ? formatLongDate : undefined} valueFormatters={valueFormatters} />}
                />
                {series.map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2}
                    fill={s.filled === false ? 'none' : `url(#${gradientIdBase}-${s.key})`}
                    dot={false}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
            {series.length > 1 && (
              <Widget.Legend className="pt-3">
                {series.map((s) => (
                  <Widget.Legend.Item key={s.key} color={s.color} label={s.name} />
                ))}
              </Widget.Legend>
            )}
          </>
        )}
      </Widget.Content>
    </Widget>
  )
}
